import { useState, useEffect, useContext } from "react";
import {
  Button,
  Flex,
  Typography,
  App,
  Modal,
  Spin,
  Space,
  Badge,
  Pagination,
} from "antd";

import { ApartmentOutlined } from "@ant-design/icons";

import PlusIcon from "@/icons/PlusIcon";
import SettingsIcon from "@/icons/SettingsIcon";

import UnitsTable from "@/components/tables/UnitsTable";
import UnitModal from "@/components/modals/UnitModal";
import RootUnitModal from "@/components/modals/RootUnitModal";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import SearchFrom3 from "@/components/inputs/SearchFrom3";
import CustomSelect from "@/components/inputs/CustomSelect";

import { UserContext } from "@/context/UserContext";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { getList, deleteItem, getUnitsOptions } from "@/api/units";
import { getUnitTypeOptions } from "@/api/unitTypes";
import { getEmployeesOptions } from "@/api/employees";

const LOCAL_STORAGE_KEY = "unitsTableParams";

const NOT_FILLED_OPTION = {
  uuid: "notFilled",
  name: "Не заполнено",
  label: "Не заполнено",
};

// Начальные параметры таблицы вынесены из компонента
const INITIAL_TABLE_PARAMS = {
  pagination: {
    current: 1,
    pageSize: 10,
    placement: ["bottomLeft"],
    showSizeChanger: true,
  },
  filters: {
    search: null,
    unit_type_uuid: null,
    parent_uuid: null,
    manager_uuid: null,
  },
};

const { Title } = Typography;

// Страница Рабочих групп (раньше это были отделы, теперь у РГ есть типы)
// Содержит список РГ с возможностью добавления, редактирования и удаления
const Units = () => {
  const { user } = useContext(UserContext);
  const { message } = App.useApp();

  // Данные
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [optionsPending, setOptionsPending] = useState(true);

  const [tableParams, setTableParams] = useState(INITIAL_TABLE_PARAMS);

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    data,
    tableParams.pagination.pageSize,
  ]);

  const storageKey = `${user.user_uuid}_${LOCAL_STORAGE_KEY}`;

  // Списки для фильтров
  const [unitTypes, setUnitTypes] = useState([]); // типы РГ
  const [units, setUnits] = useState([]); // все РГ
  const [employees, setEmployees] = useState([]); // авторы

  // Окно создания/редактирования
  const [isModalShow, setIsModalShow] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // Окно настройки Головной организации
  const [isSettingsModalShow, setIsSettingsModalShow] = useState(false);

  //  initial fetch
  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line
  }, []);

  const fetchOptions = async () => {
    setOptionsPending(true);

    try {
      const [{ types }, { units }, { employees }] = await Promise.all([
        getUnitTypeOptions({ deleted: false }),
        getUnitsOptions(),
        getEmployeesOptions({ is_dismissed: false }),
      ]);

      setUnitTypes(
        types
          .filter((type) => !type.is_personal)
          .map((item) => {
            const { name, is_hierarchical } = item;
            return {
              ...item,
              label: (
                <Space>
                  <span>{name}</span>
                  <Badge
                    count={
                      is_hierarchical ? (
                        <ApartmentOutlined
                          style={{
                            color: "var(--color-primary)",
                          }}
                        />
                      ) : (
                        0
                      )
                    }
                  />
                </Space>
              ),
            };
          }),
      );
      setUnits(units);
      setEmployees(
        employees.map((item) => {
          const { lastname, firstname, patronymic } = item.employee;
          return {
            ...item.employee,
            label: `${lastname || ""} ${firstname || ""} ${patronymic || ""}`,
          };
        }),
      );

      // Инициализация параметров из localStorage
      const localParams =
        JSON.parse(localStorage.getItem(storageKey)) || INITIAL_TABLE_PARAMS;

      setTableParams(localParams);
      fetchData(localParams);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setOptionsPending(false);
    }
  };

  // Запрос на сервер для получения данных
  const fetchData = async (tParams) => {
    setIsLoading(true);

    const currentParams = tParams || tableParams;
    const params = {
      page: currentParams.pagination.current,
      perpage: currentParams.pagination.pageSize,
      sort: currentParams.sort,
      ...currentParams.filters,
    };

    try {
      const { units, total, page, perPage } = await getList(params);
      setData(units);

      const updatedTableParams = {
        ...currentParams,
        pagination: {
          ...currentParams.pagination,
          total,
          current: page,
          pageSize: perPage,
        },
      };
      setParams(updatedTableParams);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Обработчик для смены фильтров
  const handleFilterChange = async (name, val) => {
    updateFilters(
      tableParams,
      {
        [name]: val || null,
      },
      true,
    );
  };

  const updateFilters = (localParams, newFilters, resetPage = false) => {
    const updatedFilters = { ...localParams.filters, ...newFilters };

    const updatedTableParams = {
      ...localParams,
      filters: updatedFilters,
      pagination: {
        ...localParams.pagination,
        current: resetPage ? 1 : localParams.pagination.current,
      },
    };

    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  // Устанавливаем параметры таблицы и сохраняем их в localStorage
  const setParams = (params) => {
    setTableParams(params);

    // Сохраняем параметры пагинации в localStorage
    localStorage.setItem(storageKey, JSON.stringify(params));
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    const updatedTableParams = {
      ...tableParams,

      pagination: {
        ...tableParams.pagination,
        current: pagination.current || tableParams.pagination.current,
        pageSize: pagination.pageSize || tableParams.pagination.pageSize,
      },
      ...(sorter?.order
        ? {
            sort: `${sorter.order === "descend" ? "-" : ""}${sorter.field}`,
          }
        : { sort: null }),
    };

    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  const handlePaginationChange = (current, pageSize) => {
    // При изменении pageSize сбрасываем страницу на 1
    const shouldResetPage = pageSize !== tableParams.pagination.pageSize;

    const updatedTableParams = {
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: shouldResetPage ? 1 : current,
        pageSize,
      },
    };
    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  // Обработчик добавления новой РГ
  const handleAddNew = () => {
    setCurrentItem(null);
    setIsModalShow(true);
  };

  // Обработчик редактирования РГ
  const handleEditItem = (item) => {
    setCurrentItem(item);
    setIsModalShow(true);
  };

  // Обработчик сохранения РГ (кнопка в модальном окне)
  const handleSaveItem = () => {
    setIsModalShow(false);
    fetchData();
  };

  // Обработчик удаления РГ
  const handleDeleteItem = (item) => {
    if (item.members?.length > 0) {
      message.open({
        type: "error",
        content:
          "Нельзя удалить рабочую группу, в которой есть участники. Удалите участников и попробуйте снова.",
        duration: 10,
      });
      return;
    } else {
      Modal.confirm({
        title: `Вы уверены, что хотите удалить рабочую группу ${item.name} ?`,
        content: "Все вложенные группы будут отсоединены.",
        onOk() {
          handleConfirmDelete(item);
        },
        okText: "Удалить",
        cancelText: "Отмена",
      });
    }
  };

  // Действительно удаляем отдел после подтверждения
  const handleConfirmDelete = async (item) => {
    setIsLoading(true);
    try {
      await deleteItem({ uuid: item.uuid });
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  // Обработчик установки головной организации
  const handleSetRoot = () => {
    setIsSettingsModalShow(false);
    fetchData();
  };

  return (
    <>
      {optionsPending ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Flex vertical gap={25} className="h-100">
            {/* Хлебные крошки и заголовок */}
            <Flex vertical gap={10}>
              <Breadcrumbs
                items={[
                  {
                    title: "Административная панель",
                  },
                  {
                    title: "Рабочие группы",
                  },
                ]}
              />

              <Flex gap={20} align="center" justify="space-between">
                <Title level={2} className="no-margin">
                  Рабочие группы
                </Title>

                <Flex gap={10} align="center">
                  <Button
                    type="text"
                    color="default"
                    icon={<SettingsIcon />}
                    onClick={() => setIsSettingsModalShow(true)}
                  >
                    Настроить
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusIcon />}
                    onClick={handleAddNew}
                  ></Button>
                </Flex>
              </Flex>
            </Flex>

            {/* Поиск и фильтры */}
            <Flex gap={8} align="center">
              <SearchFrom3
                placeholder="Поиск"
                style={{ width: 250 }}
                defaultValue={tableParams.filters?.search}
                onSearch={(search) => handleFilterChange("search", search)}
              />
              <CustomSelect
                placeholder="Тип рабочей группы"
                fieldNames={{ value: "uuid" }}
                showSearch
                optionFilterProp="name"
                allowClear
                popupMatchSelectWidth={false}
                options={unitTypes}
                value={tableParams.filters?.unit_type_uuid}
                onChange={(val) => handleFilterChange("unit_type_uuid", val)}
                style={{ width: 170 }}
              />
              <CustomSelect
                placeholder="Родительская рабочая группа"
                fieldNames={{
                  label: "name",
                  value: "uuid",
                  key: "uuid",
                }}
                optionFilterProp="name"
                allowClear
                showSearch
                popupMatchSelectWidth={false}
                options={[NOT_FILLED_OPTION, ...units]}
                value={tableParams.filters?.parent_uuid}
                onChange={(val) => handleFilterChange("parent_uuid", val)}
                style={{ width: 230 }}
              />
              <CustomSelect
                placeholder="Руководитель"
                fieldNames={{ value: "uuid" }}
                allowClear
                showSearch
                optionFilterProp="label"
                popupMatchSelectWidth={false}
                options={[NOT_FILLED_OPTION, ...employees]}
                value={tableParams.filters?.manager_uuid}
                onChange={(val) => handleFilterChange("manager_uuid", val)}
                style={{ width: 170 }}
              />
            </Flex>

            <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
              <UnitsTable
                data={data}
                loading={isLoading}
                pagination={false}
                onChange={handleTableChange}
                onEditItem={handleEditItem}
                onDeleteItem={handleDeleteItem}
                scroll={{ x: "100%", y: tableHeight - headerHeight }}
              />
            </div>

            <div style={{ minHeight: 32 }}>
              <Pagination
                {...tableParams.pagination}
                onChange={handlePaginationChange}
              />
            </div>
          </Flex>

          {isModalShow && (
            <UnitModal
              data={currentItem}
              visible={isModalShow}
              onSave={handleSaveItem}
              onCancel={() => setIsModalShow(false)}
            />
          )}

          {isSettingsModalShow && (
            <RootUnitModal
              visible={isSettingsModalShow}
              onSave={handleSetRoot}
              onCancel={() => setIsSettingsModalShow(false)}
            />
          )}
        </>
      )}
    </>
  );
};

export default Units;
